
import { Component, Prop, Ref, Vue } from 'vue-property-decorator'
import ButtonBase from '@/components/atoms/ButtonBase.vue'
import PanelFrame from '@/components/molecules/PanelFrame.vue'
import TextSearch from '@/components/molecules/TextSearch.vue'
import RoundSelect from '@/components/molecules/RoundSelect.vue'
import StudentProgressTable from '@/components/organisms/manage/StudentProgressTable.vue'
import PageLinkButton from '@/components/organisms/PageLinkButton.vue'
import { SelectItem } from '@/models/SelectItem'
import { StudentProgress } from '@/models/manage/StudentProgress'

/**
 * 生徒進捗確認パネル
 */
@Component({
  components: {
    ButtonBase,
    PanelFrame,
    TextSearch,
    RoundSelect,
    StudentProgressTable,
    PageLinkButton,
  },
})
export default class StudentProgressPanel extends Vue {
  /** 学年フィルター */
  @Ref()
  gradeFilter!: RoundSelect

  /** 教室フィルター */
  @Ref()
  branchFilter!: RoundSelect

  /** コースフィルター */
  @Ref()
  courseFilter!: RoundSelect

  /** クラスフィルター */
  @Ref()
  classFilter!: RoundSelect

  /** 検索欄 */
  @Ref()
  textSearch!: TextSearch

  /** 教科フィルター */
  @Ref()
  subjectFilter!: RoundSelect

  /** 進捗集計単位のフィルター(教材・知識MAPの系統図を選択) */
  @Ref()
  progressUnitFilter!: RoundSelect

  /** 生徒進捗テーブル */
  @Ref()
  studentProgressTable!: StudentProgressTable

  /** 通常学習タブの状態: trueならactive */
  @Prop({ default: false })
  private isGeneralLearningTabActive!: boolean

  /** 知識MAPタブの状態: trueならactive */
  @Prop({ default: false })
  private isKnowledgeMapTabActive!: boolean

  /** 生徒情報一覧 */
  @Prop()
  private studentList!: StudentProgress[]

  /** 学年フィルターの選択肢 */
  @Prop()
  private filterGradeList!: SelectItem[]

  /** 学年フィルターの初期選択値 */
  @Prop()
  private defaultGardeCode!: string

  /** 教室フィルターの選択肢 */
  @Prop()
  private filterBranchList?: SelectItem[]

  /** コースフィルターの選択肢 */
  @Prop()
  private filterAcademyCourseList!: SelectItem[]

  /** クラスフィルターの選択肢 */
  @Prop()
  private filterAcademyClassList!: SelectItem[]

  /** クラスフィルターのdisabled制御 */
  @Prop()
  private isClassFilterDisabled!: boolean

  /** 教科フィルターの選択肢 */
  @Prop()
  private filterSubjectList!: SelectItem[]

  /** 教科フィルターの初期選択値 */
  @Prop()
  private defaultSubjectCode!: string

  /** 進捗集計単位フィルターの選択肢 */
  @Prop()
  private filterProgressUnitList?: SelectItem[]

  /** ページ数 */
  @Prop()
  private pageLength!: number

  /** 現在のページ */
  @Prop()
  private currentPage!: number

  /** 通常学習タブクリック時のイベント */
  @Prop()
  private eventClickGeneralLearningTab?: () => void

  /** 知識MAPタブクリック時のイベント */
  @Prop()
  private eventClickKnowledgeMapTab?: () => void

  /** 学年フィルター変更時のイベント */
  @Prop()
  private changeGradeFilter!: () => Promise<void>

  /** 教室フィルター変更時のイベント */
  @Prop()
  private changeBranchFilter?: () => Promise<void>

  /** コースフィルター変更時のイベント */
  @Prop()
  private changeCourseFilter!: () => Promise<void>

  /** クラスフィルター変更時のイベント */
  @Prop()
  private changeClassFilter!: () => Promise<void>

  /** 検索ボタン押下時の処理 */
  @Prop()
  private eventClickSearch!: () => Promise<void>

  /** 教科フィルター変更時のイベント */
  @Prop()
  private changeSubjectFilter!: () => Promise<void>

  /** 進捗集計単位フィルター変更時のイベント */
  @Prop()
  private changeProgressUnitFilter?: () => Promise<void>

  /** 編集エリア表示イベント */
  @Prop()
  private eventClickDetailButton!: (userId: number) => Promise<void>

  /** ページリンクボタン押下時のイベント */
  @Prop()
  private changePage!: (value: number) => Promise<void>

  /**
   * 検索文字列
   *
   * @returns 入力された文字列
   */
  public textInputed(): string {
    return this.textSearch.text
  }

  /**
   * 検索文字列を設定
   *
   * @param value 指定文字列
   */
  public setCurrentSearchText(value: string): void {
    this.textSearch.setText(value)
  }

  /**
   * 選択中の学年
   *
   * @returns 学年コード
   */
  public currentGrade(): string {
    return this.gradeFilter.selectedValue()
  }

  /**
   * 選択中の教室
   *
   * @returns 教室コード
   */
  public currentBranch(): string {
    return this.branchFilter.selectedValue()
  }

  /**
   * 教室選択の選択値を設定
   *   選択肢の変更時にselectの値を直接書き換える
   *
   * @param value 選択値指定
   */
  public setCurrentBranchValue(value: string): void {
    this.branchFilter.setCurrentValue(value)
  }

  /**
   * 選択中のコース
   *
   * @returns コースコード
   */
  public currentCourse(): string {
    return this.courseFilter.selectedValue()
  }

  /**
   * コース選択の選択値を設定
   *   選択肢の変更時にselectの値を直接書き換える
   *
   * @param value 選択値指定
   */
  public setCurrentCourseValue(value: string): void {
    this.courseFilter.setCurrentValue(value)
  }

  /**
   * 選択中のクラス
   *
   * @returns クラスコード
   */
  public currentClass(): string {
    return this.classFilter.selectedValue()
  }

  /**
   * クラス選択の選択値を設定
   *   選択肢の変更時にselectの値を直接書き換える
   *
   * @param value 選択値指定
   */
  public setCurrentClassValue(value: string): void {
    this.classFilter.setCurrentValue(value)
  }

  /**
   * 選択中の教科
   *
   * @returns 教科コード
   */
  public currentSubject(): string {
    return this.subjectFilter.selectedValue()
  }

  /**
   * 選択中の進捗集計単位
   *
   * @returns コード
   */
  public currentProgressUnit(): string {
    return this.progressUnitFilter.selectedValue()
  }

  /**
   * 進捗集計単位選択の選択値を設定
   *   選択肢の変更時にselectの値を直接書き換える
   *
   * @param value 選択値指定
   */
  public setCurrentProgressUnitValue(value: string): void {
    this.progressUnitFilter.setCurrentValue(value)
  }
}
